import { useState, useRef, useCallback, useEffect, ReactNode } from 'react'
import type { SwipeActionsConfig, SwipeAction } from './DataTable.types'
import {
  SwipeableCardContainer,
  SwipeActionsContainer,
  SwipeActionButton,
  SwipeableCardContent,
} from './DataTable.styles'

interface SwipeableCardProps<T> {
  item: T
  children: ReactNode
  swipeActions?: SwipeActionsConfig<T>
  /** Callback when another card starts swiping (to close this one) */
  onSwipeStart?: () => void
  /** Whether this card should be closed */
  forceClose?: boolean
}

export function SwipeableCard<T>({
  item,
  children,
  swipeActions,
  onSwipeStart,
  forceClose,
}: SwipeableCardProps<T>) {
  const [translateX, setTranslateX] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const startX = useRef(0)
  const startY = useRef(0)
  const currentX = useRef(0)
  const isDragging = useRef(false)
  const hasDecidedDirection = useRef(false)
  const isHorizontal = useRef(false)

  const threshold = swipeActions?.threshold ?? 80
  const leftActions = swipeActions?.left ?? []
  const rightActions = swipeActions?.right ?? []
  const hasLeftActions = leftActions.length > 0
  const hasRightActions = rightActions.length > 0

  // Calculate max swipe distances
  const maxLeftSwipe = hasRightActions ? rightActions.length * 80 : 0
  const maxRightSwipe = hasLeftActions ? leftActions.length * 80 : 0

  // Force close when another card starts swiping
  useEffect(() => {
    if (forceClose && translateX !== 0) {
      setIsAnimating(true)
      setTranslateX(0)
      setTimeout(() => setIsAnimating(false), 200)
    }
  }, [forceClose, translateX])

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!hasLeftActions && !hasRightActions) return

      const touch = e.touches[0]
      startX.current = touch.clientX
      startY.current = touch.clientY
      currentX.current = touch.clientX
      isDragging.current = true
      hasDecidedDirection.current = false
      isHorizontal.current = false
      setIsAnimating(false)
    },
    [hasLeftActions, hasRightActions]
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging.current) return

      const touch = e.touches[0]
      const deltaX = touch.clientX - startX.current
      const deltaY = touch.clientY - startY.current

      // Decide direction on first significant movement
      if (!hasDecidedDirection.current) {
        const absX = Math.abs(deltaX)
        const absY = Math.abs(deltaY)

        if (absX > 10 || absY > 10) {
          hasDecidedDirection.current = true
          isHorizontal.current = absX > absY

          if (isHorizontal.current) {
            onSwipeStart?.()
          }
        }
      }

      // Only process horizontal swipes
      if (!isHorizontal.current) return

      currentX.current = touch.clientX

      // Calculate new position with resistance at edges
      let newTranslateX = translateX + deltaX

      // Apply resistance when past the action buttons
      if (newTranslateX < -maxLeftSwipe) {
        const over = -maxLeftSwipe - newTranslateX
        newTranslateX = -maxLeftSwipe - over * 0.3
      } else if (newTranslateX > maxRightSwipe) {
        const over = newTranslateX - maxRightSwipe
        newTranslateX = maxRightSwipe + over * 0.3
      }

      // Prevent swiping in wrong direction
      if (!hasRightActions && newTranslateX < 0) newTranslateX = 0
      if (!hasLeftActions && newTranslateX > 0) newTranslateX = 0

      setTranslateX(newTranslateX)

      // Prevent scroll while swiping horizontally
      e.preventDefault()
    },
    [
      translateX,
      maxLeftSwipe,
      maxRightSwipe,
      hasLeftActions,
      hasRightActions,
      onSwipeStart,
    ]
  )

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return
    isDragging.current = false

    if (!isHorizontal.current) return

    setIsAnimating(true)

    if (translateX < -threshold && hasRightActions) {
      // Snap open to reveal right actions
      setTranslateX(-maxLeftSwipe)
    } else if (translateX > threshold && hasLeftActions) {
      // Snap open to reveal left actions
      setTranslateX(maxRightSwipe)
    } else {
      // Snap back to closed
      setTranslateX(0)
    }

    setTimeout(() => setIsAnimating(false), 200)
  }, [
    translateX,
    threshold,
    hasLeftActions,
    hasRightActions,
    maxLeftSwipe,
    maxRightSwipe,
  ])

  const handleActionClick = useCallback(
    (action: SwipeAction<T>, e: React.MouseEvent) => {
      e.stopPropagation()
      action.onAction(item)
      setIsAnimating(true)
      setTranslateX(0)
      setTimeout(() => setIsAnimating(false), 200)
    },
    [item]
  )

  // If no swipe actions, just render children
  if (!hasLeftActions && !hasRightActions) {
    return <>{children}</>
  }

  return (
    <SwipeableCardContainer ref={containerRef}>
      {/* Left actions (revealed on swipe right) */}
      {hasLeftActions && (
        <SwipeActionsContainer $side="left">
          {leftActions.map((action, index) => (
            <SwipeActionButton
              key={index}
              $color={action.color}
              onClick={(e) => handleActionClick(action, e)}
              aria-label={action.label}
            >
              {action.icon}
              <span>{action.label}</span>
            </SwipeActionButton>
          ))}
        </SwipeActionsContainer>
      )}

      {/* Right actions (revealed on swipe left) */}
      {hasRightActions && (
        <SwipeActionsContainer $side="right">
          {rightActions.map((action, index) => (
            <SwipeActionButton
              key={index}
              $color={action.color}
              onClick={(e) => handleActionClick(action, e)}
              aria-label={action.label}
            >
              {action.icon}
              <span>{action.label}</span>
            </SwipeActionButton>
          ))}
        </SwipeActionsContainer>
      )}

      {/* Card content that slides */}
      <SwipeableCardContent
        $translateX={translateX}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={{
          transition: isAnimating ? 'transform 0.2s ease-out' : 'none',
        }}
      >
        {children}
      </SwipeableCardContent>
    </SwipeableCardContainer>
  )
}

export default SwipeableCard
